import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { CustomListVersionHistory } from 'dashboard/models/report-template';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Heading, Paragraph, Span } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

export const ReportTemplateVersionTimeline = ({
  versions,
  currentVersionId,
  onClose,
  onVersionSelect,
}: {
  versions: CustomListVersionHistory[];
  currentVersionId: string;
  onClose: () => void;
  onVersionSelect: (id: string) => void;
}) => {
  const displayVersions: CustomListVersionHistory[] = versions.sort(
    (a, b) =>
      DateTime.fromISO(b['created-at']).diff(DateTime.fromISO(a['created-at']), 'seconds').seconds,
  );

  return (
    <VersionHistoryTimeline>
      <VersionHistoryHeader>
        <Heading size='sm'>Version history</Heading>
        <Button
          type='button'
          id='report-template-version-timeline__close'
          width='auto'
          variant='inverse'
          onClick={onClose}
        >
          <XIcon />
        </Button>
      </VersionHistoryHeader>
      <VersionTimelineBody>
        {displayVersions.map((version, idx) => (
          <Version
            key={version.id}
            id={`report-template-version-timeline__version-select-${version.id}`}
            first={idx === 0}
            last={idx === versions.length - 1}
            type='button'
            selected={currentVersionId === version.id}
            onClick={() => onVersionSelect(version.id)}
          >
            <div>
              <Circle
                isSelected={currentVersionId === version.id}
                fill='none'
                width={ICON_SIZE}
                height={ICON_SIZE}
                viewBox='0 0 10 10'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle cx='5' cy='5' r='4' fill='currentColor' />
              </Circle>
            </div>
            <VersionTitle>
              <Span>Updated</Span>
              <Paragraph>
                {DateTime.fromISO(version['created-at']).toFormat(DATE_AND_TIME_FORMAT)}
              </Paragraph>
            </VersionTitle>
            <List>
              <li>
                <Paragraph>{getConfigMessage(version, displayVersions[idx + 1])}</Paragraph>
              </li>
              {version.description && (
                <li>
                  <Paragraph>{`Description: ${version.description}`}</Paragraph>
                </li>
              )}
            </List>
          </Version>
        ))}
      </VersionTimelineBody>
    </VersionHistoryTimeline>
  );
};

const getConfigMessage = (
  currentVersion?: CustomListVersionHistory,
  previousVersion?: CustomListVersionHistory,
) => {
  if (previousVersion == null) {
    return 'New template created';
  }

  if (currentVersion == null || currentVersion['uploaded-at'] === previousVersion['uploaded-at']) {
    return 'Configuration changed';
  }

  return `New source file (${currentVersion['file-name']})`;
};

const VersionHistoryTimeline = styled.div`
  width: 400px;
  height: 100vh;
  z-index: ${Z_INDICES.tooltip};
  top: 0;
  right: 0;
  position: absolute;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.secondary};
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const VersionHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing[2]} 0 ${Spacing[2]} ${Spacing[5]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const VersionTimelineBody = styled.div`
  overflow-y: auto;
  max-height: 100%;
`;

const PADDING_SIZE = BASE * 3;
const ICON_SIZE = 10;
const ICON_COLUMN_SIZE = 30;

const Version = styled.button<{ first?: boolean; last?: boolean; selected?: boolean }>`
  position: relative;
  background-color: ${({ selected, theme }) =>
    selected ? `${Color.CorePurple100}` : `${theme.color.background.primary}`};
  display: grid;
  grid-template-columns: ${ICON_COLUMN_SIZE}px auto;
  text-align: left;
  width: 100%;
  padding: ${BASE * 2.5}px ${PADDING_SIZE}px;

  &:hover {
    cursor: pointer;
    background-color: ${Color.CorePurple100};

    & svg {
      color: ${Color.CorePurple700};
      stroke: ${Color.CorePurple700};
    }
  }

  &:after {
    content: '';
    position: absolute;
    width: 1px;
    height: 100%;
    left: ${PADDING_SIZE + ICON_SIZE / 2 - 0.5}px; // -0.5 pixel to account for line thickness
    bottom: ${({ first, last }) => {
      if (first) {
        return '-30%';
      } else if (last) {
        return '70%';
      } else {
        return 0;
      }
    }};
    background-color: ${({ theme }) => theme.color.border.secondary};
  }
`;

const VersionTitle = styled.h4`
  display: flex;
  column-gap: ${Spacing[2]};

  & span {
    ${paragraphMixins.md};
    font-weight: ${FontWeight.Medium};
  }
`;

const List = styled.ul`
  grid-column: 2;
  margin-left: ${ICON_COLUMN_SIZE}px;
  color: ${({ theme }) => theme.color.text.secondary};
`;

const Circle = styled.svg<{ isSelected?: boolean }>`
  position: relative;
  z-index: 1; // Elevate svg above :after element
  stroke: ${({ isSelected, theme }) =>
    isSelected ? `${Color.CorePurple700}` : `${theme.color.text.primary}`};
  color: ${({ isSelected, theme }) =>
    isSelected ? `${Color.CorePurple700}` : `${theme.color.background.primary}`};
`;
